import React, { useCallback, useEffect, useState } from 'react';
import styles from './Product.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { getProjectById, getRelatedProjects } from '../../../services/projectService';
import { CustomButton, CustomCard, CustomFooter, CustomImage, CustomLink, CustomLoader, CustomMoreSection, CustomRadio, CustomText, CustomTitle } from '../../../components';
import image from '../../../assets/img/project_image.jpg';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { downloadProject, toggleLikeProject } from '../../../services/storeService';
import { addHistory } from '../../../services/historyService';
import { historyTypes } from '../../../enums/historyTypes.enum';
import { ReactComponent as HearthIcon } from '../../../assets/icons/icon_heart.svg';
import { ReactComponent as HearthActiveIcon } from '../../../assets/icons/icon_heart_active.svg';
import { ReactComponent as AtafIcon } from '../../../assets/icons/icon_ataf.svg';
import { ReactComponent as ShareIcon } from '../../../assets/icons/icon_share_dark.svg';
import CustomChips from '../../../components/customChips/CustomChips';
import colors from '../../../styles/colors';

const Product = () => {

    const { projectId } = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const userId : number = useSelector((state: RootState) => state.auth.userId);

    const [isLoading, setIsLoading] = useState(true);

    const [project, setProject] = useState<project|null>(null);
    const [relatedProjects, setRelatedProjects] = useState<any>([]);
    const [isAtaf, setIsAtaf] = useState<boolean>(false);
    const [isProjectLiked, setIsProjectLiked] = useState<boolean>(false);
    const [isDownloadSectionOpen, setIsDownloadSectionOpen] = useState<boolean>(false);
    const [downloadType, setDownloadType] = useState<string>('downloadImage');

    const downloadOptions: any = [
        { value: 'downloadImage', label: t('store.download_image'), checked: true },
        { value: 'downloadDeri', label: t('store.download_deri') }
    ]

    const handleGetProject = useCallback(async () => {
        try {
            if (projectId) {
                const projectData = await getProjectById(projectId);

                const likes = projectData.likes;
                const isLiked = likes.some((item: any) => item.id === userId);

                const isAtaf = projectData.categories.some((item: any) => item.documentId === "f6fcbvxliew1qp6gy1so37jj");

                setIsAtaf(isAtaf);
                setProject(projectData);
                setIsProjectLiked(isLiked);
            }
        } catch (error) {
            console.error('Error fetching project:', error);
            throw error;
        }
    }, [projectId, userId]);

    const handleGetRelatedProjects = useCallback(async () => {
        try {
            const categories = project?.categories.map((item: any) => item.title);
            const relatedProjects = await getRelatedProjects(categories, projectId!);

            const formatedRelatedProjects = relatedProjects.map((item: any) => ({
                ...item,
                isLiked : item.likes.some((item: any) => item.id === userId)
            }));

            setRelatedProjects(formatedRelatedProjects);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [project?.categories, projectId, userId]);

    const handleGetContent = useCallback(async () => {
        setIsLoading(true);
        await handleGetProject();
        await handleGetRelatedProjects();
        setIsLoading(false);
    }, [handleGetProject, handleGetRelatedProjects]);

    useEffect(() => {
        handleGetContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleLikeProject = () => {
        try {
            if (userId && project) {
                const reqProject: project = {
                    id: projectId,
                    documentId: projectId,
                    title: project.title,
                    description: project.description,
                    isPublic: true,
                    price: project.price,
                    likes: project.likes,
                }

                setIsProjectLiked(!isProjectLiked);
                toggleLikeProject(reqProject, userId);
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    const handleLikeRelatedProject = (project: any) => {
            if (userId) {
                setRelatedProjects((prevList: any[])=>
                    prevList.map(currentProject =>
                        currentProject.documentId === project.documentId
                        ? {
                            ...currentProject,
                            isLiked: !currentProject.isLiked,
                        }
                        : currentProject
                    )
                );
        
                toggleLikeProject(project, userId);
            }
        }

    const handleDownloadProject = () => {
        try {
            if (project != null) {
                addHistory(String(userId), projectId!, historyTypes.DOWNLOAD);
                
                let fileUrl = '';
                let fileName = '';

                switch(downloadType) {
                    case "downloadImage":
                        fileUrl = project.image.url;
                        fileName = project.image.name;
                        break;
                    case "downloadDeri":
                    default:
                        fileUrl = project.deriFile.url;
                        fileName = project.deriFile.name;
                        break;
                }

                downloadProject(`${process.env.REACT_APP_API_BASE_URL}${fileUrl}`, fileName);
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    const goToProductPage = (projectId: number) => {
        navigate(`/store/${projectId}`);
    }

    const handleShare = async (url: string, title: string, description: string) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: description,
                    url: url,
                });
            } catch (error) {
                console.error('Erreur lors du partage :', error);
            }
        } else {
            alert("Le partage n'est pas supporté par ce navigateur.");
        }
    }

    const getProjectUrl = (id: string) => {
        return `${process.env.REACT_APP_API_BASE_URL}/store/${id}`;
    }

    return (
        <div>
            { !isLoading && project &&
                <>
                    <div className={styles.content}>
                        <div className={styles.containerProject}>
        
                            <div>
                                <CustomText><b><CustomLink className={styles.storeLink} to='/store'>{t('store.store')}</CustomLink></b> | <b>{project.title}</b></CustomText>

                                <CustomImage
                                    alt=''
                                    src={`${process.env.REACT_APP_API_BASE_URL}${project.image?.url}` || image}
                                    className={styles.projectImage}
                                />
                            </div>

                            <div className={styles.projectDetails}>
                                <CustomChips backgroundColor={project.price > 0 ? colors.gold : colors.blue}>
                                    {project.price > 0 ? t('store.premium') : t('store.free')}
                                </CustomChips>

                                <div className={styles.titleContainer}>
                                    <CustomTitle className={styles.projectTitle}>{project.title}</CustomTitle>
                                    
                                    <div className={styles.likeContainer}>
                                        <span role='button' tabIndex={0} onClick={() => handleLikeProject()} className={styles.likeIcon}>
                                            { isProjectLiked ? <HearthActiveIcon /> : <HearthIcon />}
                                        </span>
                                        <CustomText>{project.likes?.length}</CustomText>
                                    </div>
                                </div>

                                <div className={styles.subtitleContainer}>
                                    <CustomText><b>{t('store.created_by')}</b> : <span className={styles.username}>{project.author?.username}</span></CustomText>
                                    {isAtaf && <AtafIcon />}
                                </div>

                                <CustomText className={styles.description}>{project.description}</CustomText>

                                <div>
                                    {project.categories.map((item: any) => <span key={item.id} className={styles.category}>{item.title}</span>)}
                                </div>

                                <CustomText className={styles.price}><b>{t('store.price')} :</b> {project.price} €</CustomText>

                                { isDownloadSectionOpen &&
                                    <div aria-live='polite'>
                                        <CustomRadio
                                            id='download'
                                            name="download"
                                            onChange={(value: string) => setDownloadType(value)}
                                            options={downloadOptions}
                                            className={styles.container_radio}
                                        />
                                    </div>
                                }

                                <div className={styles.buttonsContainer}>
                                    { !isDownloadSectionOpen &&
                                        <CustomButton sx={{padding: '0.8rem 1.5rem', marginRight: '1.5rem'}} onClick={() => setIsDownloadSectionOpen(true)} disabled={!userId || !project.deriFile?.id} >{t('store.download_project')}</CustomButton>
                                    }

                                    { isDownloadSectionOpen &&
                                        <CustomButton sx={{padding: '0.8rem 1.5rem', marginRight: '1.5rem'}} onClick={() => handleDownloadProject()} disabled={!userId || !project.deriFile?.id} >{t('store.confirm_download_project')}</CustomButton>
                                    }

                                    <CustomButton sx={{padding: '0.8rem'}} color={"info"} onClick={() => handleShare(getProjectUrl(project.documentId!) ,project.title, project.description)}><ShareIcon /></CustomButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.containerRelated}>
                        <CustomTitle center className={styles.relatedTitle}>{t('store.may_also_like')}</CustomTitle>

                        <div className={styles.relatedProjects}>
                            {relatedProjects.map((item: any) => {
                                return (
                                    <CustomCard
                                        key={item.id}
                                        data={item}
                                        imageAlt=""
                                        hoverText={t('store.see_project')}
                                        imageUrl={`${process.env.REACT_APP_API_BASE_URL}${item.image?.url}`}
                                        onClick={() => goToProductPage(item.documentId)}
                                        onLike={() => handleLikeRelatedProject(item)}
                                        onShare={handleShare}
                                        className={styles.relatedCard}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <CustomMoreSection isStore />
                    <CustomFooter />
                </>
            }

            {isLoading &&
                <div className={styles.container_loader}>
                    <CustomLoader isLoading={isLoading} loaderType='skeleton' />
                </div>
            }
        </div>
    );
}

export default Product;